import {
    Day,
    DayWindow,
    DEFAULT_WORKING_HOURS,
    SOURCE_INDIVIDUAL,
    SOURCE_TEAM,
    Team,
    WorkingHours,
} from "../../../types/core";
import { Member } from "../../../types/member";
import { Job } from "../../../types/job";
import { dateToDayBrowserTimezone, datePlusDays, dayToDateBrowserTimezone } from "../../../utils/time";
import { getFirstWeekday } from "../../timeline/timelineUtils";

export const FALLBACK_ON_SITE_SECONDS = 30 * 60;

const WEEKDAY_KEYS: Array<keyof Omit<WorkingHours, "source">> = [
    "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

// Returns the 7 day-ints of the week containing `anchor`, starting on the locale's first weekday.
// Uses real Date arithmetic so month/year boundaries are handled correctly.
export function weekDaysFor(anchor: Day): Day[] {
    const anchorDate = dayToDateBrowserTimezone(anchor);
    const dayOfWeek = anchorDate.getDay(); // 0 = Sunday
    const firstWeekday = getFirstWeekday();
    const offset = dayOfWeek >= firstWeekday
        ? dayOfWeek - firstWeekday
        : dayOfWeek - firstWeekday + 7;
    const start = datePlusDays(anchorDate, -offset);
    return Array.from({ length: 7 }, (_, i) => dateToDayBrowserTimezone(datePlusDays(start, i)));
}

export function shiftWeek(anchor: Day, weeks: number): Day {
    return dateToDayBrowserTimezone(datePlusDays(dayToDateBrowserTimezone(anchor), weeks * 7));
}

// Same resolution as WeekCalendar: individual > team > default.
export function resolveWorkingHours(member: Member | undefined, team: Team | undefined): WorkingHours {
    if (!member?.working_hours) {
        return DEFAULT_WORKING_HOURS;
    }
    if (member.working_hours.source === SOURCE_INDIVIDUAL) {
        return member.working_hours;
    }
    if (member.working_hours.source === SOURCE_TEAM && team?.working_hours) {
        return team.working_hours;
    }
    return DEFAULT_WORKING_HOURS;
}

// Per-date overrides (keyed by the full day int as string) win over the weekday default.
export function windowForDay(workingHours: WorkingHours, overrides: Member["working_hours_overrides"], day: Day): DayWindow {
    const override = overrides?.[`${day}`];
    if (override) {
        return override;
    }
    const weekday = dayToDateBrowserTimezone(day).getDay();
    return workingHours[WEEKDAY_KEYS[weekday]];
}

function hhmmToMinutes(hhmm: number): number {
    return Math.floor(hhmm / 100) * 60 + (hhmm % 100);
}

// Capacity in minutes for a day window; 0 = day off.
export function capacityMinutes(window: DayWindow): number {
    if (window.start === 0 && window.end === 0) {
        return 0;
    }
    return Math.max(0, hhmmToMinutes(window.end) - hhmmToMinutes(window.start));
}

// Committed service minutes (travel excluded — fallback when a route isn't computed).
export function committedMinutes(jobs: Job[]): number {
    return Math.round(jobs.reduce((sum, job) => sum + (job.onSiteSeconds || FALLBACK_ON_SITE_SECONDS), 0) / 60);
}

// Runs `worker` over `items` with at most `limit` in flight; bails between items
// when `isCancelled()` turns true (e.g. the week changed). Never rejects — each
// item's failure is swallowed so one bad route can't abort the rest.
export async function runThrottled<T>(
    items: T[],
    limit: number,
    worker: (item: T) => Promise<unknown>,
    isCancelled: () => boolean,
): Promise<void> {
    let index = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (index < items.length && !isCancelled()) {
            const item = items[index++];
            try {
                await worker(item);
            } catch {
                /* routing failure surfaces via routingFailureReasonState; keep going */
            }
        }
    });
    await Promise.all(runners);
}

export function formatHours(minutes: number): string {
    if (minutes < 60) {
        return `${minutes}m`;
    }
    const hours = minutes / 60;
    return `${hours % 1 === 0 ? hours : hours.toFixed(1)}h`;
}

export function formatDayLabel(day: Day): { weekday: string; date: string } {
    const date = dayToDateBrowserTimezone(day);
    return {
        weekday: date.toLocaleDateString(undefined, { weekday: "short" }),
        date: date.toLocaleDateString(undefined, { day: "numeric" }),
    };
}

export function formatWeekRange(days: Day[]): string {
    if (days.length === 0) {
        return "";
    }
    const start = dayToDateBrowserTimezone(days[0]);
    const end = dayToDateBrowserTimezone(days[days.length - 1]);
    // the language's own range: "Nov 2 – 8, 2026", "2.–8. Nov. 2026", "2–8 de nov de 2026"
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).formatRange(start, end);
}
